import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ErrorComponent } from '@/components/common/ErrorComponent';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useSetBreadcrumbs } from '@/components/layout/breadcrumbs';

import { ContactsGrid } from './-components/list/ContactsGrid';

const contactsSearchSchema = z.object({
  page: z.number().catch(0),
  pageSize: z.number().catch(25),
  /** Set to 1 to open the two-step "New contact" modal as an overlay. */
  new: z.number().optional().catch(undefined),
  /**
   * The whole filter as a compact query string (parseQuery.ts) — the single
   * source of truth, parsed into a ConditionTree for POST /contacts/_search.
   */
  q: z.string().optional().catch(undefined),
  /**
   * Free-text quick search (name or email). Maps to the backend's
   * ContactSearchRequest.Query, AND-merged with `q`'s conditions.
   */
  query: z.string().optional().catch(undefined),
  /**
   * Server-side sort. `sortBy` is a table column id (mapped to a Mongo field in
   * ContactsGrid); `sortDir` is the direction. URL-persisted so sorting survives
   * pagination and is the single source of truth.
   */
  sortBy: z.string().optional().catch(undefined),
  sortDir: z.enum(['asc', 'desc']).optional().catch(undefined),
  /**
   * Contact id whose quick panel (general note + comments) is open to the right
   * of the table. Toggled by clicking a row; URL-persisted like audit-logs.
   */
  selected: z.string().optional().catch(undefined),
});

function ContactsRoute() {
  useSetBreadcrumbs([{ label: 'Contacts' }]);
  return <ContactsGrid />;
}

export const Route = createFileRoute('/contacts/')({
  validateSearch: contactsSearchSchema,
  component: ContactsRoute,
  errorComponent: ErrorComponent,
  pendingComponent: () => <LoadingSpinner message="Loading contacts..." />,
});
